import * as RadixToast from '@radix-ui/react-toast';
import { CheckCircle2, Info, TriangleAlert, X, XCircle } from 'lucide-react';
import * as React from 'react';
import { cn } from '../utils/cn';

const iconMap = {
  success: CheckCircle2,
  error: XCircle,
  warning: TriangleAlert,
  info: Info,
};

const accentMap: Record<keyof typeof iconMap, string> = {
  success: 'text-emerald-600',
  error: 'text-red-600',
  warning: 'text-amber-600',
  info: 'text-brand-600',
};

export interface ToastEntry {
  id: string;
  title: string;
  description?: string;
  intent?: keyof typeof iconMap;
  durationMs?: number;
}

export const ToastViewport = () => (
  <RadixToast.Viewport className="fixed bottom-6 right-6 z-50 flex w-96 max-w-[calc(100vw-2rem)] flex-col gap-3 outline-none" />
);

export interface ToastProps extends RadixToast.ToastProps {
  entry: ToastEntry;
}

export function Toast({ entry, className, ...props }: ToastProps) {
  const intent = entry.intent ?? 'info';
  const Icon = iconMap[intent];
  return (
    <RadixToast.Root
      duration={entry.durationMs ?? 5000}
      className={cn(
        'lectura-card flex items-start gap-3 p-4 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-right-4',
        className,
      )}
      {...props}
    >
      <Icon className={cn('mt-0.5 size-5 shrink-0', accentMap[intent])} />
      <div className="flex-1">
        <RadixToast.Title className="text-sm font-semibold">{entry.title}</RadixToast.Title>
        {entry.description ? (
          <RadixToast.Description className="mt-1 text-xs text-[var(--color-foreground-muted)]">
            {entry.description}
          </RadixToast.Description>
        ) : null}
      </div>
      <RadixToast.Close
        className="rounded-full p-1 text-[var(--color-foreground-subtle)] transition-colors hover:bg-[var(--color-surface-subtle)] hover:text-[var(--color-foreground)]"
        aria-label="Bezárás"
      >
        <X className="size-4" />
      </RadixToast.Close>
    </RadixToast.Root>
  );
}

export const ToastProvider = RadixToast.Provider;
